/** @forge-header
 * @id          totalParsers
 * @role        Total ticker parsers | all sports over/under market parsing
 * @pattern     pure-fn
 * @purity      none
 * @units       n/a
 * @upstream    kalshiTicker/types:ParsedTicker,GameInfo,TotalInfo,TickerCategory | kalshiTicker/helpers:labelToSport,parseFallback | nbaConsolidated/teamNames:teamNameFromCode
 * @downstream  kalshiTicker/parse
 * @exports     parseNbaTotal:fn | parseNflTotal:fn | parseNhlTotal:fn | parseNcaaMbTotal:fn | parseNcaaWbTotal:fn
 * @test        parse.test.ts (indirect)
 */

import type { ParsedTicker, GameInfo, TotalInfo, TickerCategory } from '../types';
import { parseDateFromTicker } from '../types';
import { labelToSport, parseFallback } from '../helpers';
import { teamNameFromCode } from '../teamNames';

/** NBA total (over/under): KXNBATOTAL-26FEB04BOSMIA-O */
export function parseNbaTotal(raw: string): ParsedTicker {
  return parseTotalGeneric(raw, 'KXNBATOTAL', 'nba-total', 'NBA');
}

/** NFL total: KXNFLTOTAL-26FEB08SEANE-O */
export function parseNflTotal(raw: string): ParsedTicker {
  return parseTotalGeneric(raw, 'KXNFLTOTAL', 'nfl-total', 'NFL');
}

/** NHL total: KXNHLTOTAL-26FEB08SEATOR-O */
export function parseNhlTotal(raw: string): ParsedTicker {
  return parseTotalGeneric(raw, 'KXNHLTOTAL', 'nhl-total', 'NHL');
}

/** NCAA Men's Basketball total */
export function parseNcaaMbTotal(raw: string): ParsedTicker {
  return parseTotalGeneric(raw, 'KXNCAAMBTOTAL', 'ncaa-total', 'NCAAMB');
}

/** NCAA Women's Basketball total */
export function parseNcaaWbTotal(raw: string): ParsedTicker {
  return parseTotalGeneric(raw, 'KXNCAAWBTOTAL', 'ncaa-total', 'NCAAWB');
}

/** Generic total parser for all sports */
function parseTotalGeneric(
  raw: string,
  series: string,
  category: TickerCategory,
  label: string
): ParsedTicker {
  const parts = raw.split('-');
  if (parts.length < 2) {
    return parseFallback(raw);
  }

  const mid = parts[1] ?? '';
  if (mid.length < 13) {
    return parseFallback(raw);
  }

  const date = parseDateFromTicker(mid.slice(0, 7));
  const teamCodes = mid.slice(7);

  if (teamCodes.length < 6) {
    return parseFallback(raw);
  }

  const awayCode = teamCodes.slice(0, 3);
  const homeCode = teamCodes.slice(3, 6);
  const outcome = parts[2]?.toUpperCase() ?? null;

  const game: GameInfo = { awayCode, homeCode };

  // Get team names for sports with lookup tables
  const sport = labelToSport(label);
  if (sport) {
    game.awayName = teamNameFromCode(awayCode, sport);
    game.homeName = teamNameFromCode(homeCode, sport);
  }

  const awayDisplay = game.awayName ?? awayCode;
  const homeDisplay = game.homeName ?? homeCode;

  const side = outcome === 'U' ? 'under' : 'over';
  const sideLabel = side === 'over' ? 'Over' : 'Under';

  const shortLabel = `${label} Total: ${awayCode} @ ${homeCode} ${sideLabel}`;
  const fullLabel = `${label} Total: ${awayDisplay} @ ${homeDisplay} · ${sideLabel}`;

  // Note: Actual total value comes from market data, not ticker
  const total: TotalInfo = {
    totalValue: 0, // Will be populated from market data
    side,
  };

  return {
    raw,
    category,
    series,
    date,
    game,
    player: null,
    spread: null,
    total,
    tennis: null,
    outcome,
    shortLabel,
    fullLabel,
  };
}
